import { zodResolver } from '@hookform/resolvers/zod';
import {
  useCreateWorkspace,
  useGetDeployment,
  useGetWorkspacePreflightStatus,
  useListDeploymentInfras,
  useListDeployments,
  useRunWorkspacePreflight,
  useStreamReleaseEvents,
  WorkspaceEnvironment,
} from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Checkbox,
  cn,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@sunstone/ui';
import { ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { PageHeader } from '@/components/ui/PageHeader';
import { PreflightChecklistDialog } from '@/components/ui/PreflightChecklistDialog';
import { ReleaseCombobox } from '@/components/ui/ReleaseCombobox';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { useDebounce } from '@/hooks/useDebounce';
import { useSlugSync } from '@/hooks/useSlugSync';
import { flags } from '@/lib/flags';
import { workspaceDisplayNameSchema, workspaceSlugify, workspaceSlugSchema } from '@/lib/schemas';

const ENVIRONMENTS: { value: WorkspaceEnvironment; label: string; description: string }[] = [
  {
    value: WorkspaceEnvironment.DEVELOPMENT,
    label: 'Development',
    description: 'Testing and development. First to receive platform upgrades.',
  },
  {
    value: WorkspaceEnvironment.STAGING,
    label: 'Staging',
    description: 'Pre-production validation. Receives upgrades after development.',
  },
  {
    value: WorkspaceEnvironment.PRODUCTION,
    label: 'Production',
    description: 'Business-critical cluster. Last to receive upgrades.',
  },
];

const schema = z.object({
  displayName: workspaceDisplayNameSchema,
  deploymentId: z.string().min(1, 'Select a deployment'),
  slug: workspaceSlugSchema,
  environment: z.union([
    z.literal(WorkspaceEnvironment.DEVELOPMENT),
    z.literal(WorkspaceEnvironment.STAGING),
    z.literal(WorkspaceEnvironment.PRODUCTION),
  ]),
  releaseId: z.string().min(1, 'Release version is required'),
  infra: z.string().optional(),
  createInfra: z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

export function NewWorkspacePage() {
  const navigate = useNavigate();
  const { tenantId, deploymentId } = useParams<{ tenantId: string; deploymentId: string }>();
  const { token } = useAuthToken();
  const { hasPermission } = useAuthorization();
  useStreamReleaseEvents(token);

  const [deploymentSearch, setDeploymentSearch] = useState('');
  const debouncedDeploymentSearch = useDebounce(deploymentSearch);
  const [infraSearch, setInfraSearch] = useState('');

  // Read by the resolver's superRefine at validation time — kept in sync below
  // once `infraOptions` is known, so `infra` becomes truly required (not
  // just checked on submit) once the selected deployment needs it.
  const requireInfraRef = useRef(false);
  const resolver = useMemo(
    () =>
      zodResolver(
        schema.superRefine((values, ctx) => {
          if (requireInfraRef.current && !values.infra) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['infra'],
              message: 'Infrastructure is required for this deployment',
            });
          }
          // New infra names must satisfy DuploCloud's rule (start with a letter,
          // lowercase letters/digits/hyphens only, max 12) — mirrors the backend
          // ValidateInfraName so the operator gets immediate feedback.
          if (
            values.createInfra &&
            values.infra &&
            (values.infra.length > 12 || !/^[a-z]([a-z0-9-]*[a-z0-9])?$/.test(values.infra))
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['infra'],
              message:
                'Must start with a letter and use only lowercase letters, digits, and hyphens (max 12 characters)',
            });
          }
        }),
      ),
    [],
  );

  const form = useForm<FormValues>({
    resolver,
    mode: 'onChange',
    defaultValues: {
      deploymentId: deploymentId ?? '',
      displayName: '',
      slug: '',
      environment: WorkspaceEnvironment.DEVELOPMENT,
      releaseId: '',
      infra: '',
      createInfra: false,
    },
  });

  const deploymentIdValue = form.watch('deploymentId');
  const createInfra = form.watch('createInfra') ?? false;
  const resolvedDeploymentId = deploymentIdValue;

  const { data: deploymentData } = useGetDeployment(resolvedDeploymentId ?? '', token);
  const deployment = deploymentData?.deployment;

  // Live list of the deployment portal's infras (Duplo plans) for the selector.
  const { data: infrasData } = useListDeploymentInfras(resolvedDeploymentId, token);

  // Install-gate preflight state (the onSubmit gate below starts + polls this).
  const startPreflight = useRunWorkspacePreflight(token);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightStarted, setPreflightStarted] = useState(false);
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);
  // Poll the run keyed on the slug we started preflight for.
  const preflightStatus = useGetWorkspacePreflightStatus(
    resolvedDeploymentId ?? '',
    pendingValues?.slug ?? '',
    token,
    preflightOpen && preflightStarted,
  );

  // Manual "workspace readiness" check (gated by flags.showPreflightButton):
  // pre-deploy-workspace preview against the typed slug, separate from the
  // install gate so their state doesn't clash.
  const runReadiness = useRunWorkspacePreflight(token);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [readinessStarted, setReadinessStarted] = useState(false);
  const [readinessRanSlug, setReadinessRanSlug] = useState('');
  const readinessStatus = useGetWorkspacePreflightStatus(
    resolvedDeploymentId ?? '',
    readinessRanSlug,
    token,
    readinessOpen && readinessStarted,
  );

  function runReadinessCheck() {
    const slug = form.getValues('slug').trim();
    if (!slug || !resolvedDeploymentId) return;
    // Disable the poll until this run is confirmed started, so it never queries a
    // slug whose workflow hasn't started yet (which would 404 -> "No run found").
    setReadinessStarted(false);
    setReadinessRanSlug(slug);
    setReadinessOpen(true);
    runReadiness.mutate(
      { deploymentId: resolvedDeploymentId, slug, mode: 'db', scope: 'pre-deploy-workspace' },
      { onSuccess: () => setReadinessStarted(true) },
    );
  }

  const { data: deploymentsData } = useListDeployments(
    token,
    undefined,
    undefined,
    { search: debouncedDeploymentSearch, pageSize: 20 },
    !deploymentId,
  );
  const deployments = deploymentsData?.deployments ?? [];

  const resolvedTenantId =
    tenantId || deployments.find((d) => d.id === deploymentIdValue)?.tenantId || '';

  const createWorkspace = useCreateWorkspace(token);
  const syncSlug = useSlugSync(form, workspaceSlugify);

  const infraOptions = useMemo(
    () =>
      (infrasData?.infras ?? []).map((infra) => ({
        id: infra.name,
        displayName: infra.name,
      })),
    [infrasData],
  );
  requireInfraRef.current = infraOptions.length > 0;

  // SearchableCombobox renders with shouldFilter={false} (filtering is
  // server-side for the deployment picker below), so this infra list needs its
  // own client-side filter by the search text.
  const filteredInfraOptions = useMemo(
    () =>
      infraOptions.filter((item) =>
        item.displayName.toLowerCase().includes(infraSearch.trim().toLowerCase()),
      ),
    [infraOptions, infraSearch],
  );

  // Clear a stale selection when the field becomes hidden (e.g. the deployment
  // was switched to one with no selectable infras), so it can't leak into the
  // create payload for a deployment it was never selected for.
  useEffect(() => {
    if (infraOptions.length === 0 && form.getValues('infra')) {
      form.setValue('infra', '', { shouldValidate: true });
    }
  }, [infraOptions.length, form]);

  // onSubmit runs the preflight gate first (when enabled): it starts the async
  // preflight run and opens the checklist dialog, which polls for the report. The
  // actual workspace creation happens in doCreate, invoked from the dialog once
  // the operator proceeds (carrying acknowledgeUnknowns for a NEEDS_DECISION
  // outcome). When runPreflightOnWorkspaceInstall is off, preflight is skipped
  // entirely and creation proceeds directly with no gating.
  function onSubmit(values: FormValues) {
    setPendingValues(values);
    if (!flags.runPreflightOnWorkspaceInstall) {
      void doCreate(false, values);
      return;
    }
    setPreflightStarted(false);
    setPreflightOpen(true);
    startPreflight.mutate(
      {
        deploymentId: resolvedDeploymentId ?? '',
        slug: values.slug,
        releaseId: values.releaseId,
        mode: 'db',
        scope: 'pre-deploy-workspace',
      },
      { onSuccess: () => setPreflightStarted(true) },
    );
  }

  async function doCreate(acknowledgeUnknowns: boolean, values: FormValues | null = pendingValues) {
    if (!values) return;
    const payload = {
      deploymentId: resolvedDeploymentId ?? '',
      environment: values.environment,
      displayName: values.displayName.trim(),
      slug: values.slug,
      releaseId: values.releaseId,
      infra: values.infra,
      createInfra: values.createInfra,
      acknowledgeUnknowns,
    };
    const result = await createWorkspace.mutateAsync(payload);
    setPreflightOpen(false);
    const workspaceId = result.workspace?.id;
    navigate(
      workspaceId && resolvedTenantId && resolvedDeploymentId
        ? `/tenants/${resolvedTenantId}/deployments/${resolvedDeploymentId}/workspaces/${workspaceId}`
        : resolvedTenantId && resolvedDeploymentId
          ? `/tenants/${resolvedTenantId}/deployments/${resolvedDeploymentId}`
          : '/deployments',
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title="New Workspace"
        description={
          deployment
            ? `Add a workspace to ${deployment.displayName}.`
            : 'Provision a new Kubernetes cluster workspace.'
        }
      />

      <Card>
        <CardContent className="py-6 space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {createWorkspace.error && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {createWorkspace.error.message ??
                      'Failed to create workspace. Please try again.'}
                  </AlertDescription>
                </Alert>
              )}

              <FormField
                control={form.control}
                name="deploymentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Deployment <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      {deploymentId ? (
                        <Input value={deployment?.displayName ?? deploymentId} disabled />
                      ) : (
                        <SearchableCombobox
                          value={field.value}
                          onChange={(val) => {
                            field.onChange(val);
                            setDeploymentSearch('');
                          }}
                          items={deployments.map((d) => ({ id: d.id, displayName: d.displayName }))}
                          search={deploymentSearch}
                          onSearchChange={setDeploymentSearch}
                          placeholder="Select a deployment…"
                          searchPlaceholder="Search deployments…"
                          emptyMessage="No deployments found."
                          autoOpen
                          autoOpenDelay={600}
                        />
                      )}
                    </FormControl>
                    {!deploymentId && (
                      <FormDescription>Showing first 20 — type to search.</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Display name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. Production US-East"
                        autoFocus={!!deploymentId}
                        onChange={(e) => syncSlug(e.target.value, field.onChange)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      URL Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. prod-us-east" maxLength={12} />
                    </FormControl>
                    <FormDescription>Used in URLs and API references.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {infraOptions.length > 0 && (
                <div className="space-y-3">
                  <FormField
                    control={form.control}
                    name="infra"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Infrastructure <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          {createInfra ? (
                            <Input
                              value={field.value ?? ''}
                              onChange={(e) =>
                                form.setValue('infra', e.target.value, { shouldValidate: true })
                              }
                              placeholder="New infrastructure name (e.g. acme-infra)"
                            />
                          ) : (
                            <SearchableCombobox
                              value={field.value ?? ''}
                              onChange={(val) => {
                                form.setValue('infra', val, { shouldValidate: true });
                                setInfraSearch('');
                              }}
                              items={filteredInfraOptions}
                              search={infraSearch}
                              onSearchChange={setInfraSearch}
                              placeholder="Select an infrastructure…"
                              searchPlaceholder="Search infrastructure…"
                              emptyMessage="No infrastructure options available."
                            />
                          )}
                        </FormControl>
                        <FormDescription>
                          {createInfra
                            ? 'A new infrastructure with this name will be created for this workspace.'
                            : 'Choose which infrastructure to deploy this workspace into.'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="createInfra"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={!!field.value}
                            onCheckedChange={(checked) => {
                              form.setValue('createInfra', checked === true, {
                                shouldValidate: true,
                              });
                              // Clear any selected/typed value when switching modes.
                              form.setValue('infra', '', { shouldValidate: true });
                              setInfraSearch('');
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal">Create new infrastructure</FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <FormField
                control={form.control}
                name="environment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Environment <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      {/* Card-style toggle: no @sunstone/ui ToggleGroup equivalent yet */}
                      <div className="grid gap-3 sm:grid-cols-3">
                        {ENVIRONMENTS.map((env) => (
                          <button
                            key={env.value}
                            type="button"
                            onClick={() => field.onChange(env.value)}
                            className={cn(
                              'flex flex-col items-start gap-1 rounded-lg border px-4 py-4 text-left transition-all',
                              field.value === env.value
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
                            )}
                          >
                            <span className="text-sm font-semibold">{env.label}</span>
                            <span className="text-xs leading-relaxed">{env.description}</span>
                          </button>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="releaseId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Release Version <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <ReleaseCombobox
                        token={token}
                        value={field.value}
                        onChange={field.onChange}
                        deployableOnly
                        autoSelectFirst
                        placeholder="Select a release version…"
                      />
                    </FormControl>
                    <FormDescription>
                      The BAIC release build to provision into this workspace. Sorted newest first.{' '}
                      <Link
                        to="/releases"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline underline-offset-4"
                      >
                        View all releases
                      </Link>
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {flags.showPreflightButton && (
                <div className="space-y-2 rounded-md border bg-muted/30 p-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">Workspace readiness</p>
                    <p className="text-xs text-muted-foreground">
                      Check the target account has no conflicting leftovers for this workspace slug
                      before installing. Read-only — it doesn't create the workspace.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={runReadinessCheck}
                    disabled={!form.watch('slug')?.trim() || runReadiness.isPending}
                  >
                    <ShieldCheck className="h-4 w-4" />
                    {runReadiness.isPending ? 'Starting checks…' : 'Run workspace-readiness checks'}
                  </Button>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    navigate(
                      resolvedTenantId && resolvedDeploymentId
                        ? `/tenants/${resolvedTenantId}/deployments/${resolvedDeploymentId}`
                        : '/deployments',
                    )
                  }
                >
                  ← Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={
                    !form.formState.isValid ||
                    startPreflight.isPending ||
                    createWorkspace.isPending ||
                    !hasPermission('create:workspace')
                  }
                >
                  {startPreflight.isPending ? 'Starting checks…' : 'Create Workspace'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <PreflightChecklistDialog
        open={preflightOpen}
        onOpenChange={setPreflightOpen}
        status={preflightStatus.data}
        isLoading={
          startPreflight.isPending ||
          createWorkspace.isPending ||
          (preflightStarted && !preflightStatus.data)
        }
        onRerun={() => {
          if (!pendingValues) return;
          setPreflightStarted(false);
          startPreflight.mutate(
            {
              deploymentId: resolvedDeploymentId ?? '',
              slug: pendingValues.slug,
              releaseId: pendingValues.releaseId,
              mode: 'db',
              scope: 'pre-deploy-workspace',
            },
            { onSuccess: () => setPreflightStarted(true) },
          );
        }}
        onProceed={doCreate}
      />

      {flags.showPreflightButton && (
        <PreflightChecklistDialog
          open={readinessOpen}
          onOpenChange={setReadinessOpen}
          status={readinessStatus.data}
          isLoading={runReadiness.isPending || (readinessStarted && !readinessStatus.data)}
          onRerun={runReadinessCheck}
          onProceed={() => {}}
          previewOnly
        />
      )}
    </div>
  );
}
